import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, Phone, Mail } from "lucide-react";
import { useChatContext } from "@/contexts/ChatContext";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { displayCompanyName } from "@/lib/displayCompanyName";

interface Props {
  loadId: string;
  awardedBroker: {
    user_id: string | null;
    company_name: string | null;
    contact_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

export function MessagesTab({ loadId, awardedBroker }: Props) {
  const { openChatWithUser } = useChatContext();

  if (!awardedBroker || !awardedBroker.user_id) {
    return (
      <Card className="p-6 text-center">
        <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm font-medium mb-1">No conversation yet</p>
        <p className="text-xs text-muted-foreground">
          Messaging unlocks after a broker is awarded. Manage bids from the Bids tab.
        </p>
      </Card>
    );
  }

  const name = awardedBroker.contact_name || displayCompanyName(awardedBroker.company_name) || "Broker";

  return (
    <Card className="p-4 space-y-3">
      <div>
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Awarded Broker</p>
        <p className="text-sm font-semibold">{displayCompanyName(awardedBroker.company_name) || "—"}</p>
        {awardedBroker.contact_name && <p className="text-xs text-muted-foreground">{awardedBroker.contact_name}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button
          size="sm"
          className="gap-1.5 text-xs"
          style={{ backgroundColor: "hsl(var(--brand-teal))" }}
          onClick={() => openChatWithUser(awardedBroker.user_id!, name, undefined, loadId)}
        >
          <MessageSquare className="h-3.5 w-3.5" /> Open Chat
        </Button>
        {awardedBroker.phone && (
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" asChild>
            <a href={`tel:${awardedBroker.phone}`}>
              <Phone className="h-3.5 w-3.5" /> {formatPhoneNumber(awardedBroker.phone)}
            </a>
          </Button>
        )}
        {awardedBroker.email && (
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" asChild>
            <a href={`mailto:${awardedBroker.email}`}>
              <Mail className="h-3.5 w-3.5" /> Email
            </a>
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground border-t border-border pt-2">
        Chat opens in the floating messenger and stays threaded to this load.
      </p>
    </Card>
  );
}
